import { useEffect, useState } from 'react';
import { ApiError } from '../../api/client';
import { getOverview, type Overview } from '../../api/admin';
import { Alert, Card, ErrorState, LoadingState } from '../ui';
import { AdminBackground } from './AdminBackground';
import { AdminMetrics } from './AdminMetrics';
import { AdminStatus } from './AdminStatus';

const LABELS: Record<string, string> = {
  users: 'People',
  administrators: 'Administrators',
  moderators: 'Moderators',
  deactivated: 'Deactivated accounts',
  stories: 'Stories',
  published_stories: 'Published',
  featured_stories: 'Featured',
  datasets: 'Datasets',
  dashboards: 'Dashboards',
  categories: 'Categories',
  tags: 'Tags',
  audit_entries: 'Recorded decisions',
  awaiting_review: 'Waiting for review',
  hidden_stories: 'Hidden stories',
  open_reports: 'Open reports',
  comments: 'Comments',
  hidden_comments: 'Hidden comments',
  likes: 'Likes',
};

/** What this instance holds — and, as plainly, what it cannot manage yet. */
export function AdminOverview() {
  const [overview, setOverview] = useState<Overview | null>(null);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    getOverview()
      .then(setOverview)
      .catch((caught: unknown) => setError(caught instanceof ApiError ? caught : null));
  }, []);

  if (error) {
    return <ErrorState title="We could not read the overview" description={error.message} />;
  }
  if (!overview) {
    return <LoadingState label="Reading the instance…" />;
  }

  return (
    <>
      {/* What the instance is running on, before what it holds (Phase 28) —
          and then whether anything is actually doing the work (Phase 32). */}
      <AdminStatus />
      <AdminBackground />
      {/* And what it has been spending doing it (Phase 33). */}
      <AdminMetrics />

      <ul className="sc-admin__counts">
        {Object.entries(overview.counts).map(([key, value]) => (
          <li key={key}>
            <Card>
              <p className="sc-admin__count">{value.toLocaleString()}</p>
              <p className="sc-admin__count-label">{LABELS[key] ?? key}</p>
            </Card>
          </li>
        ))}
      </ul>

      {overview.not_yet_available.map((pending) => (
        <Alert key={pending.key} tone="info" title={`${pending.label}: not here yet`}>
          {pending.note}
        </Alert>
      ))}
    </>
  );
}
